import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  street?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const AddressSection: React.FC<Props> = ({ values, updateField, errors }) => {
  return (
    <fieldset css={sectionBoxStyles}>
      <legend css={sectionTitleStyles}>Address</legend>

      <div css={fieldStyles}>
        <label css={labelStyles} htmlFor="street">
          Street address <span css={requiredStyles}>*</span>
        </label>
        <Textfield
          id="street"
          value={values.street}
          onChange={(e) => updateField('street', (e.target as HTMLInputElement).value)}
        />
        {errors?.street && <div css={errorStyles}>{errors.street}</div>}
      </div>

      <div css={twoColGridStyles}>
        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="city">
            City <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="city"
            value={values.city}
            onChange={(e) => updateField('city', (e.target as HTMLInputElement).value)}
          />
          {errors?.city && <div css={errorStyles}>{errors.city}</div>}
        </div>

        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="state-province">
            State / Province <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="state-province"
            value={values.stateProvince}
            onChange={(e) => updateField('stateProvince', (e.target as HTMLInputElement).value)}
          />
          {errors?.stateProvince && <div css={errorStyles}>{errors.stateProvince}</div>}
        </div>
      </div>

      <div css={twoColGridStyles}>
        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="postal-code">
            Zip / Post code <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="postal-code"
            value={values.postalCode}
            onChange={(e) => updateField('postalCode', (e.target as HTMLInputElement).value)}
          />
          {errors?.postalCode && <div css={errorStyles}>{errors.postalCode}</div>}
        </div>

        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="country">
            Country <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="country"
            value={values.country}
            onChange={(e) => updateField('country', (e.target as HTMLInputElement).value)}
          />
          {errors?.country && <div css={errorStyles}>{errors.country}</div>}
        </div>
      </div>

      <div css={fieldTopStyles}>
        <label css={labelStyles} htmlFor="birth-date">
          Birth date
        </label>
        <Textfield
          id="birth-date"
          placeholder="MM/DD/YYYY"
          value={values.birthDate}
          onChange={(e) => updateField('birthDate', (e.target as HTMLInputElement).value)}
        />
      </div>
    </fieldset>
  );
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const sectionTitleStyles = css({
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '12px',
  letterSpacing: '0.04em',
  marginBottom: token('space.200'),
});

const twoColGridStyles = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: token('space.200'),
  marginTop: token('space.200'),
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.200'),
});

const labelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: token('space.050'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
